import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/shared/ui/Button'
import { Field } from '@/shared/ui/Field'
import { Strip } from '@/shared/ui/Strip'
import { CheckIcon } from '@/shared/ui/icons'
import type { Currency } from '@/shared/api/types'
import { coinOf, networkOf } from '@/shared/lib/currencies'
import { addDecimal, normalizeAmount } from '@/shared/lib/decimal'
import { crypto, shortAddress } from '@/shared/lib/format'
import { useCabinet, useStores, useUi } from '@/stores/RootStore'
import { feeOnTop, type WithdrawStore } from '@/stores/WithdrawStore'
import { DialogPage } from './DialogPage'
import styles from './Send.module.css'

/**
 * Отправка средств.
 *
 * «Адрес получателя» — свободное поле, как в макете: отправить можно
 * на любой адрес, сохранённые кошельки нужны только автовыводам.
 * Контрольную сумму адреса проверяет бекенд при создании заявки
 * и подписант перед подписью; здесь адрес показывается ещё раз,
 * целиком и моноширинным, на шаге подтверждения — это последний
 * экран, где опечатку ещё можно заметить.
 */
export const SendPage = observer(function SendPage({ currency }: { currency: Currency }) {
  const { withdraw: store } = useStores()
  const cabinet = useCabinet()
  const ui = useUi()

  useEffect(() => {
    store.start(currency)
    void cabinet.balances.load()
  }, [currency, store, cabinet])

  useEffect(() => () => store.dispose(), [store])

  const coin = coinOf(currency)
  const network = networkOf(currency)

  return (
    <DialogPage
      title={store.step === 'confirm' ? 'Подтверждение' : 'Отправить'}
      note={store.step === 'confirm'
        ? 'Убедитесь в корректности указанных данных и подтвердите отправление.'
        : `Отправка ${coin.ticker} в сети ${network}.`}
      /* Главное действие живёт в подвале диалога рядом с «Закрыть» —
         так в макете. В шаге подтверждения кнопка секунду неактивна:
         рефлекторный двойной клик не должен доводить до подписи. */
      actions={
        store.step === 'form' ? (
          <Button variant="primary" onClick={() => void store.review()}>Отправить</Button>
        ) : store.step === 'confirm' ? (
          <Button variant="primary" busy={store.busy}
                  disabled={store.countdown > 0}
                  onClick={() => void store.submit()}>
            Подтвердить
          </Button>
        ) : null
      }
    >
      <>
        {store.step === 'done'
          ? <Done onClose={() => ui.closeDialog()} />
          : (
            <>
              {/* Предупреждение стоит выше формы, а не под кнопкой:
                  прочитанное после ввода адреса оно уже бесполезно. */}
              <Strip tone="warn">
                Отправляйте только {coin.ticker} в сети {network}.
                Другая сеть приведёт к потере средств.
              </Strip>
              {store.step === 'form' ? <Form currency={currency} /> : <Confirm />}
            </>
          )}
      </>
    </DialogPage>
  )
})

const Form = observer(function Form({ currency }: { currency: Currency }) {
  const { withdraw: store } = useStores()
  const coin = coinOf(currency)
  const balance = store.balance

  return (
    <div className={styles.fields}>
      {/* Свободный адрес, как в макете. Опечатку ловит контрольная сумма
          сети на бекенде — и она же показывается человеку ошибкой под
          полем, а не отказом после подтверждения. */}
      <Field label="Адрес получателя" placeholder="Укажите адрес"
             value={store.address} autoComplete="off" spellCheck={false}
             onChange={(e) => store.setAddress(e.target.value)}
             error={store.errors.address ?? null} />

      {/* Сеть не выбирается: она определена валютой. Выключенное поле,
          как в макете, а не текст — так видно, что это значение, а не
          подпись, и что менять его нельзя. */}
      <Field label="Сеть" value={networkOf(currency)} disabled />

      <Field label="Сумма" placeholder="Укажите сумму" numeric inputMode="decimal"
             value={store.amount} param={coin.ticker}
             onChange={(e) => store.setAmount(e.target.value)}
             error={store.errors.amount ?? null}
             /* Порог сети — рядом с балансом, как в макете («От 0.001»).
                Мерчант должен видеть его ДО отправки формы: узнавать
                о минимуме отказом сервера, уже введя сумму и сверив
                адрес, — это лишний круг на ровном месте. */
             note={balance ? (
               <>
                 Баланс:{' '}
                 <button type="button" className={styles.balance} onClick={() => void store.useMax()}>
                   {crypto(balance.available)} {coin.ticker}
                 </button>
                 {' '}· от {crypto(balance.min_withdrawal)}
               </>
             ) : undefined} />

      <Preview />
    </div>
  )
})

/**
 * Комиссия сети и итог списания с точки зрения мерчанта.
 *
 * BTC, LTC, USDT-TRC20, GRAM, USDT-TON — комиссия СВЕРХ суммы: получатель
 * получает ровно заказанное, с баланса уходит сумма плюс комиссия,
 * посчитанная бекендом до отправки. Она же уйдёт в заявку: выросла — сервер
 * откажет. XMR — при тарифе «платит клиент» плата вычитается из самой суммы
 * и становится известна только при подписи.
 */
function feeLines(
  store: WithdrawStore, currency: Currency, ticker: string, clientPays: boolean,
): { fee: string; total: string } {
  if (feeOnTop(currency)) {
    if (store.fee === null) return { fee: '—', total: '—' }
    const total = addDecimal(normalizeAmount(store.amount), store.fee)
    return {
      fee: `${crypto(store.fee)} ${ticker}`,
      total: total === null ? '—' : `${crypto(total)} ${ticker}`,
    }
  }
  const gatewayPays = !clientPays
  return {
    fee: gatewayPays ? `0 ${ticker}` : 'вычитается из суммы',
    total: `${crypto(store.amount)} ${ticker}`,
  }
}

const Preview = observer(function Preview() {
  const { withdraw: store, auth } = useStores()
  const currency = store.currency
  if (currency === null || store.amount.trim() === '') return null
  const ticker = coinOf(currency).ticker
  const lines = feeLines(store, currency, ticker, auth.user?.client_pays_network_fee ?? true)

  return (
    <dl className={styles.preview}>
      <div className={styles.line}>
        <dt>Сумма перевода</dt>
        <dd>{crypto(store.amount)} {ticker}</dd>
      </div>
      <div className={styles.line}>
        <dt>Комиссия сети</dt>
        <dd>{lines.fee}</dd>
      </div>
      <div className={`${styles.line} ${styles.total}`}>
        <dt>Итого к списанию</dt>
        <dd>{lines.total}</dd>
      </div>
    </dl>
  )
})

const Confirm = observer(function Confirm() {
  const { withdraw: store, auth } = useStores()
  const currency = store.currency
  if (currency === null || store.address === '') return null
  const coin = coinOf(currency)
  const lines = feeLines(store, currency, coin.ticker, auth.user?.client_pays_network_fee ?? true)

  return (
    <>
      <dl className={styles.preview}>
        {/* Адрес показывается целиком и моноширинным: это последний
            экран, где ошибку ещё можно заметить. */}
        <Row label="Адрес получателя" value={store.address} mono />
        <Row label="Валюта" value={coin.ticker} />
        <Row label="Сеть" value={networkOf(currency)} />
        <Row label="Сумма перевода" value={`${crypto(store.amount)} ${coin.ticker}`} />
        <Row label="Комиссия сети" value={lines.fee} />
        <Row label="Итого к списанию" value={lines.total} />
      </dl>
    </>
  )
})

const Done = observer(function Done({ onClose }: { onClose: () => void }) {
  const { withdraw: store } = useStores()
  const navigate = useNavigate()
  const created = store.created
  if (created === null) return null
  const coin = coinOf(created.currency)

  return (
    <>
      <div className={styles.done}>
        <span className={styles.tick}><CheckIcon size={28} /></span>
        <span className={styles.doneTitle}>Заявка создана</span>
        <span className={styles.doneNote}>
          {crypto(created.amount)} {coin.ticker} на {shortAddress(created.address, 8, 6)}
        </span>
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" onClick={onClose}>К обзору</Button>
        <Button variant="primary" onClick={() => { onClose(); navigate('/app/history') }}>
          В историю
        </Button>
      </div>
    </>
  )
})

function Row({ label, value, mono, soft }: {
  label: string; value: string; mono?: boolean; soft?: boolean
}) {
  const classes = [styles.value]
  if (mono) classes.push(styles.mono)
  if (soft) classes.push(styles.soft)
  return (
    <div className={styles.line}>
      <dt>{label}</dt>
      <dd className={classes.join(' ')}>{value}</dd>
    </div>
  )
}
